import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  ListItem,
  OrderedList,
  Spinner,
} from "@chakra-ui/react";
import { useQuery, useQueryClient } from "react-query";
import { getMetricOptions } from "components/MetricInput";
import IssbContainer from "./IssbContainer";
import PageHeader from "components/text/PageHeader";
import templatePackageService from "api/issb/templatepackage.service";
import templateQuestionService from "api/issb/templatequestion.service";
import { useAuth } from "context";
import { useSearchParams, useNavigate } from "react-router-dom";
import DataTableV2 from "components/DataTable/DateTableV2";
import { routes } from "config/paths";
import { AButton } from "components/A";
import { ArrowCounterClockwise, ArrowLeft, Trash } from "@phosphor-icons/react";
import PropInput from "components/PropInput";
import { H3, H4 } from "components/text/headings";
import { SmallText } from "components/text/TextTypes";
import HighMediumLow from "api/common/HighMediumLow";
import LifecycleState from "api/common/LifecycleState";
import { v4 as uuidv4 } from "uuid";
import { numberForReference } from "./IssbDataInput/helper";
import commentService from "api/common/comment.service";

function IssbTemplateQuestionListPage() {
  const navigate = useNavigate();
  // TODO package name from url
  const [searchParams] = useSearchParams();
  const packageId = searchParams.get("packageId");

  if (!packageId) {
    console.error("No package name provided");
  }
  const { getAccessTokenSilently } = useAuth();
  const [templatePackage, setTemplatePackage] = useState(null);
  useEffect(() => {
    if (!packageId) {
      return;
    }
    getAccessTokenSilently().then((token) => {
      templatePackageService.get(packageId).then(setTemplatePackage);
    });
  }, [packageId, getAccessTokenSilently]);

  // Get the template-questions!
  const {
    data: templateQuestionsPage,
    isLoading,
    error,
  } = useQuery(
    ["template-questions", packageId],
    () => {
      return getAccessTokenSilently().then((token) => {
        return templateQuestionService.list({ query: packageId ? "packageId:" + packageId : null, size: 10000 }); // 10k - no need to paginate
      });
    },
    {
      enabled: !!getAccessTokenSilently,
    }
  );
  const tqList = templateQuestionsPage?.content;
  if (tqList) {
    // sort by issbReference
    tqList.sort((a, b) => {
      return (numberForReference(a.issbReference) || 0) - (numberForReference(b.issbReference) || 0);
    });
  }

  // get comments
  // Note: this will fetch all TQ comments, not just for this package, but that should be ok as we dont expect many comments here
  const { data: comments } = useQuery(
    ["template-question-comments"],
    () => {
      return getAccessTokenSilently().then((token) => {
        return commentService
          .list({ query: "targetTable:issb_template_question", size: 10000 })
          .then((commentsPage) => {
            return commentsPage.content;
          });
      });
    },
    {
      enabled: !!getAccessTokenSilently,
    }
  );
  const comments4tqid = {};
  comments?.forEach((c) => {
    const targetId = "" + c.targetId; // enforce string
    if (!comments4tqid[targetId]) {
      comments4tqid[targetId] = [];
    }
    comments4tqid[targetId].push(c);
  });

  const columns = [
    {
      header: "Clause",
      accessorKey: "issbReference",
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        return (
          (numberForReference(rowA.original.issbReference) || 0) -
          (numberForReference(rowB.original.issbReference) || 0)
        );
      },
    },
    {
      header: "Question",
      accessorKey: "question",
      size: "300px",
      enableSorting: false,
    },
    {
      header: "Question Group",
      accessorKey: "questionGroup",
    },
    {
      header: "Regulation",
      accessorKey: "regulation",
    },
    {
      header: "Sectors",
      accessorKey: "sectors",
    },
    {
      header: "Subsectors",
      accessorKey: "subsectors",
    },
    {
      header: "State",
      accessorKey: "state",
    },
    {
      header: "Comments",
      accessorFn: (row) => {
        const targetId = "" + row.id; // enforce string
        return comments4tqid[targetId]?.length || 0;
      },
    },
  ];

  const [addingQuestion, setAddingQuestion] = useState(false);
  const handleAddQuestion = () => {
    setAddingQuestion(true);
    templateQuestionService
      .create({
        packageId: packageId,
        state: LifecycleState.DRAFT,
      })
      .then((tq) => {
        navigate(`${routes.issbTemplateQuestionEditor.replace(":id", tq.id)}`);
        setAddingQuestion(false);
      });
  };

  return (
    <Box style={{ padding: "1rem 1rem 1rem 3rem" /*avoid the left-nav shrink button */ }}>
      <Flex direction="column" gap={2}>
        <PageHeader
          title={`Template Questions for ${packageId ? templatePackage?.name || "..." : "All packages"}`}
          subTitle="Packages collect sets of template questions e.g. for a regulation like ISSB"
        />

        <AButton
          alignSelf="flex-start"
          leftIcon={<ArrowLeft />}
          variant="link"
          color="limeShade.600"
          href={routes.issbTemplatePackage}
        >
          <Text as="span">Back to packages</Text>
        </AButton>

        {isLoading && <Text>Loading...</Text>}
        {error && <Text>Error: {error.message}</Text>}

        <Button variant="primary" onClick={handleAddQuestion} disabled={addingQuestion}>
          Add Question {addingQuestion && <Spinner size="sm" />}
        </Button>

        {tqList && (
          <DataTableV2
            data={tqList}
            enableSorting={true}
            columns={columns}
            onRowClick={(e, row) => {
              console.log("row", row, e);
              navigate(`${routes.issbTemplateQuestionEditor.replace(":id", row.original.id)}`);
            }}
          />
        )}
      </Flex>
    </Box>
  );
}

export default IssbTemplateQuestionListPage;
